import { useMempoolInfo } from '@/hooks/useBitcoinBlocks';
import { Ghost, Flame, Coins, Skull } from 'lucide-react';
import { useTheme } from '@/hooks/useTheme';

export function MempoolSpirits() {
  const { data: mempool } = useMempoolInfo();
  const { theme } = useTheme();
  const isNightmare = theme === 'nightmare';

  if (!mempool) return null;

  // Create multiple souls based on mempool size (more visible in nightmare mode)
  const maxSouls = isNightmare ? 20 : 10;
  const soulCount = Math.min(Math.floor(mempool.count / 1000), maxSouls);
  const souls = Array.from({ length: soulCount }, (_, i) => i);

  // Different ghost types for nightmare mode
  const getGhostEmoji = (index: number) => {
    if (!isNightmare) return '👻';
    const emojis = ['👻', '💀', '🦇', '🧛', '🕯️'];
    return emojis[index % emojis.length];
  };

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {souls.map((index) => (
        <div
          key={index}
          className={`absolute ${isNightmare ? 'soul-drift-fast' : 'soul-drift'}`}
          style={{
            left: `${5 + index * (isNightmare ? 4 : 8)}%`,
            bottom: 0,
            animationDelay: `${index * (isNightmare ? 0.4 : 0.8)}s`,
          }}
        >
          <span className="text-2xl">{getGhostEmoji(index)}</span>
        </div>
      ))}

      {/* Nightmare mode blood drips */}
      {isNightmare && (
        <>
          <div className="absolute top-0 left-1/4 w-1 h-20 blood-drip" style={{ animationDelay: '0.5s' }} />
          <div className="absolute top-0 right-1/3 w-1 h-20 blood-drip" style={{ animationDelay: '1.5s' }} />
          <div className="absolute top-0 left-3/4 w-1 h-20 blood-drip" style={{ animationDelay: '2.5s' }} />
        </>
      )}

      {/* Mempool info overlay */}
      <div className={`absolute top-4 right-4 rounded-lg p-3 pointer-events-auto backdrop-blur-sm border ${
        isNightmare
          ? 'bg-destructive/20 border-destructive/50'
          : 'bg-card/80 border-primary/30'
      }`}>
        <div className="flex items-center gap-2 mb-2">
          {isNightmare ? (
            <Skull className="h-5 w-5 text-destructive animate-pulse" />
          ) : (
            <Ghost className="h-5 w-5 text-primary" />
          )}
          <span className={`font-gothic text-lg ${
            isNightmare ? 'text-destructive' : 'text-primary'
          }`}>
            {isNightmare ? 'Tortured Souls' : 'Mempool Spirits'}
          </span>
        </div>

        <div className="space-y-1 text-sm">
          <div className="flex items-center justify-between gap-4">
            <span className={`flex items-center gap-1 ${
              isNightmare ? 'text-destructive/80' : 'text-muted-foreground'
            }`}>
              <Flame className="h-3 w-3" />
              {isNightmare ? 'Suffering' : 'Waiting'}
            </span>
            <span className={`font-semibold ${
              isNightmare ? 'text-destructive' : 'text-foreground'
            }`}>
              {mempool.count.toLocaleString()}
            </span>
          </div>

          <div className="flex items-center justify-between gap-4">
            <span className={`flex items-center gap-1 ${
              isNightmare ? 'text-destructive/80' : 'text-muted-foreground'
            }`}>
              <Coins className="h-3 w-3" />
              Total Fees
            </span>
            <span className={`font-semibold ${
              isNightmare ? 'text-destructive' : 'text-foreground'
            }`}>
              {(mempool.total_fee / 100000000).toFixed(4)} BTC
            </span>
          </div>

          <div className="flex items-center justify-between gap-4">
            <span className={isNightmare ? 'text-destructive/80' : 'text-muted-foreground'}>
              Size
            </span>
            <span className={`font-semibold ${
              isNightmare ? 'text-destructive' : 'text-foreground'
            }`}>
              {(mempool.vsize / 1000000).toFixed(2)} MB
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
